import React, { useState, useEffect } from 'react';
import { Container, Form, Alert } from 'react-bootstrap';
import { Auth, Hub } from 'aws-amplify';
import './Confirm.css';
import LoaderButton from '../components/LoaderButton';
import { useAppContext } from '../libs/contextLib';
import { useFormFields } from '../libs/hooksLib';
import { UiError } from '../libs/errorLib';
import { useMatomo } from '@jonkoops/matomo-tracker-react';

function Confirm() {
  const { setError } = useAppContext();
  // We initialise to true so we don't start validatring the form until the user
  // has clicked submit the first time. This prevent sannoying messages form
  // showing until the user is 'done'.
  const [isValid, setIsValid]         = useState(true);
  const [isLoading, setIsLoading]     = useState(false);
  const [isResending, setIsResending] = useState(false);
  const {user}                        = useAppContext();
  const { trackEvent }                = useMatomo();
  const [fields, handleFieldChange]   = useFormFields({
    email: '',
    code: '',
  });

  /**
   * Called after fields is updated in handleFieldChange.
   * Will validate form if state currently isn't valid.
   */
  useEffect(() => {
      if(user !== null && user.hasOwnProperty('username') && fields.email === '') {
        fields.email = user.username;
      }
      if (!isValid) {
        validateForm();
      }
    },
    // eslint-disable-next-line
    [isValid, fields, user]
  );

  function validateForm(emailOnly = false) {
    let valid = true;
    if (
      fields.email.length === 0 ||
      (
        !emailOnly &&
        fields.code.length === 0
      )
    ) {
      valid = false;
      setError(
        new UiError('confirm',  'Please ensure all form field are filled.')
      );
    }

    // Potentially clear errors.
    if (valid) {
      setError(
        new UiError('confirm',  '', 'resolved')
      );
    }
    setIsValid(valid);
    return valid;
  }

  /**
   * Function to handle the user hitting submit.
   * Tries to confirm the user using their code.
   * @param {event} event 
   */
  async function handleSubmit(event) {
    event.preventDefault();
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      await Auth.confirmSignUp(fields.email.trim(), fields.code.trim());
      // Hub doesn't send out an auth message for this.
      // Do it manually.
      Hub.dispatch(
        'custom', 
        { 
          event: 'confirm', 
          data: {}, 
          message:'' 
      });
      // Track the event.
      trackEvent({
        category: 'identity',
        action: 'confirm account',
        name: 'set',
      });
      setIsLoading(false);
    } catch (e) {
      setError({key: 'confirm', message: "Error confirming account: " + e.message, severity: 'danger'});
      setIsLoading(false);
    }
  }

  /**
   * Resends confirmation code.
   */
  async function resendCode() {
    if (!validateForm(true)) {
      return;
    }

    setIsResending(true);
    try {
      // AuthManager will pick up the effect of this.
      await Auth.resendSignUp(fields.email.trim());
      setError({key: 'confirm', message: "New code sent to " + fields.email.trim(), severity: 'info'});
      setIsResending(false);
    } catch (e) {
      setError({key: 'confirm', message: "Error confirming account: " + e.message, severity: 'danger'});
      setIsResending(false);
    }
    // Track the event.
    trackEvent({
      category: 'identity',
      action: 'confirm account',
      name: 'request',
    });
  }

  return (
    <Container fluid="sm">
      <Form className="form-confirm" onSubmit={handleSubmit}>
        <h1 className="h3 mb-2 font-weight-normal">Confirm email address</h1> 

        <Alert variant="primary" className="mb-2">
          A confirmation code will be sent to the email address you used to register.
        </Alert>

        <Form.Control 
          autoFocus
          id="email"
          placeholder="Email address"
          type="email"
          autoComplete="username"
          value={fields.email}
          onChange={handleFieldChange}
        />
        <Form.Label htmlFor="email" className="form-label visually-hidden">Email</Form.Label>
        <Form.Control
          id="code"
          placeholder="Code"
          type="text"
          autoComplete="one-time-code"
          value={fields.code}
          onChange={handleFieldChange}
        />
        <Form.Label htmlFor="code" className="form-label visually-hidden">Confirmation code</Form.Label>
        <div className="d-grid gap-2 my-2">
          <LoaderButton
            size="large"
            type="submit"
            isLoading={isLoading}
          >
            Confirm
          </LoaderButton>

          <LoaderButton
            variant="secondary"
            size="large"
            isLoading={isResending}
            onClick={() => resendCode()}
          >
            Resend code
          </LoaderButton>
        </div>
      </Form>
    </Container>
  );
}

export default Confirm;